import { z } from 'zod';
import { COUNTRY_CODES } from './countries';

const EU = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
] as const;

/**
 * Tax ID types Stripe accepts on a Customer, by billing country (collected in our own profile, synced
 * through the API). Deliberately short: the countries customers come from, plus the common VAT/GST regimes. The
 * migration seeds `tax_id_types` with the same pairs and a test compares them. Only `ca_gst_hst`, `ca_qst` and the
 * VAT/GST types change a Stripe Tax calculation; `us_ein`, `ca_bn` and the PST numbers are recorded only.
 */
export const TAX_ID_TYPES_BY_COUNTRY: Readonly<Record<string, readonly string[]>> = {
  US: ['us_ein'],
  CA: ['ca_bn', 'ca_gst_hst', 'ca_pst_bc', 'ca_pst_mb', 'ca_pst_sk', 'ca_qst'],
  GB: ['gb_vat'],
  AU: ['au_abn'],
  NZ: ['nz_gst'],
  CH: ['ch_vat'],
  NO: ['no_vat'],
  MX: ['mx_rfc'],
  ...Object.fromEntries(EU.map((c) => [c, ['eu_vat']])),
};

/** The types offered for a country; empty when we collect no tax ID there. */
export function taxIdTypesFor(country: string | null | undefined): readonly string[] {
  return (country && TAX_ID_TYPES_BY_COUNTRY[country]) || [];
}

const line = (max: number) => z.string().trim().max(max);
const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254));

/** Body of `PUT /api/orgs/:orgId/billing-profile` and the arguments of `upsert_billing_profile`. */
export const billingProfileInputSchema = z
  .strictObject({
    legalName: line(120).min(1),
    line1: line(120).min(1),
    line2: line(120).nullable().default(null),
    city: line(80).min(1),
    region: line(80).nullable().default(null),
    postalCode: line(20).min(1),
    country: z.enum(COUNTRY_CODES),
    taxIdType: z.string().nullable().default(null),
    taxIdValue: line(40).nullable().default(null),
    billingEmails: z.array(emailSchema).min(1).max(5),
    poRequired: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    const type = v.taxIdType || null;
    const value = v.taxIdValue || null;
    if ((type === null) !== (value === null)) {
      ctx.addIssue({
        code: 'custom',
        path: [type === null ? 'taxIdType' : 'taxIdValue'],
        message: 'a tax ID needs both a type and a value',
      });
    }
    if (type !== null && !taxIdTypesFor(v.country).includes(type)) {
      ctx.addIssue({
        code: 'custom',
        path: ['taxIdType'],
        message: 'tax ID type does not fit the country',
      });
    }
    if (new Set(v.billingEmails).size !== v.billingEmails.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['billingEmails'],
        message: 'billing emails must be distinct',
      });
    }
  })
  .transform((v) => ({
    ...v,
    line2: v.line2 || null,
    region: v.region || null,
    taxIdType: v.taxIdType || null,
    taxIdValue: v.taxIdValue || null,
  }));
export type BillingProfileInput = z.infer<typeof billingProfileInputSchema>;

/** A row of `public.org_billing_profiles` (select needs `billing.view`, or `staff.orgs.view`). */
export const billingProfileSchema = z.object({
  organization_id: z.uuid(),
  legal_name: z.string(),
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string(),
  region: z.string().nullable(),
  postal_code: z.string(),
  country: z.string().length(2),
  tax_id_type: z.string().nullable(),
  tax_id_value: z.string().nullable(),
  billing_emails: z.array(z.string()),
  po_required: z.boolean(),
  updated_at: z.iso.datetime({ offset: true }),
});
export type BillingProfile = z.infer<typeof billingProfileSchema>;

/**
 * `PUT /api/orgs/:orgId/billing-profile` answers 200 with this. `stripeSync`: `synced`, or `skipped` where the
 * environment has no Stripe key (local, PR). A failed sync is a 502 `billing_profile_sync_failed`: the profile IS
 * saved and the customer is re-synced before any payment.
 */
export const saveBillingProfileResponseSchema = z.object({
  profile: billingProfileSchema,
  stripeSync: z.enum(['synced', 'skipped']),
});
export type SaveBillingProfileResponse = z.infer<typeof saveBillingProfileResponseSchema>;

/** Same rule as `billing_profile_complete(org)`: what a PAYG payment and on-account mode both require (Q38). */
export function isBillingProfileComplete(p: BillingProfile | null | undefined): boolean {
  return Boolean(
    p?.legal_name && p.line1 && p.city && p.postal_code && p.country && p.billing_emails.length > 0,
  );
}
